import { readQueueVideos, type QueueVideo } from '@visious/core';

/**
 * How the Visious tabs in one browser talk to each other: a `BroadcastChannel`, which is the only
 * thing on the platform that reaches them.
 *
 * **A page cannot enumerate tabs.** That is an extension's privilege (`chrome.tabs`), so the import
 * cannot be a list of windows with a checkbox each: the importing tab **asks**, and whichever tabs
 * are listening answer. Three consequences run through everything below.
 *
 * - **There is no count to wait for.** Nobody knows how many tabs should reply, so collecting is
 *   bounded by a clock rather than by an expected number, and a partial answer is the normal answer.
 * - **A tab that is not running does not answer.** A frozen, discarded or back-forward-cached page
 *   has no JavaScript running in it, so it is simply not part of the import. It keeps its queue, and
 *   nothing on screen claims otherwise.
 * - **The channel is this origin's.** `BroadcastChannel` does not cross origins, profiles or the
 *   private-window boundary, and it does not deliver a message back to the context that posted it —
 *   so no tab has to recognise its own broadcast.
 *
 * The two-step shape — `collect` then `take` — is what keeps a tab from closing over an import that
 * never happened. A tab offers its videos, and closes only once the tab that asked says it has them:
 * the reader can still cancel at the confirmation, and a tab that answered too late to be collected
 * is not in the `take` and stays open with its list intact.
 *
 * **One channel per document, never one per component.** It is opened on the first subscriber and
 * never closed, the rule the five cross-tab stores keep (docs/specs/web-ui.md). Here it buys
 * something they do not need: the conversation outlives the React tree. Handing the videos over
 * changes what `playback/PlaybackHost.tsx` returns, which re-mounts everything under it, and a
 * channel owned by a component would be torn down in the middle of the exchange it is holding.
 */

/** The one channel name every Visious tab on this origin listens on. */
const CHANNEL = 'visious.tabs';

/**
 * Bumped when a message changes shape. A tab running another build of the app answers on the same
 * channel, and a version this one cannot read is left alone rather than guessed at: the cost of
 * ignoring it is a tab that stays open, and the cost of guessing is a tab that closes without its
 * videos having arrived.
 */
const PROTOCOL = 1;

/** Who is speaking. Per tab and per page load, which is all "which of you should close" needs. */
export const THIS_TAB = newRequestId();

export interface CollectMessage {
  readonly kind: 'collect';
  readonly request: string;
}
export interface OfferMessage {
  readonly kind: 'offer';
  readonly request: string;
  readonly tab: string;
  readonly videos: readonly QueueVideo[];
}
export interface TakeMessage {
  readonly kind: 'take';
  readonly request: string;
  /** Whose offers were taken. A tab that answered after the collection closed is not in here. */
  readonly tabs: readonly string[];
}
export interface StayedMessage {
  readonly kind: 'stayed';
  readonly request: string;
  readonly tab: string;
}

export type TabMessage = CollectMessage | OfferMessage | TakeMessage | StayedMessage;

type TabMessageHandler = (message: TabMessage) => void;

const handlers = new Set<TabMessageHandler>();

/**
 * `undefined` until the first subscriber, then the channel or `null` for a browser that has none and
 * a context where the reader's settings have blocked one. `null` is the answer the import reads as
 * "there is nothing to reach", which is the honest behaviour: an import that cannot speak to another
 * tab has nothing to import.
 */
let channel: BroadcastChannel | null | undefined;

function open(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  try {
    channel = new BroadcastChannel(CHANNEL);
  } catch {
    channel = null;
    return null;
  }
  channel.onmessage = (event: MessageEvent<unknown>) => {
    const message = readMessage(event.data);
    if (message === null) return;
    for (const handler of handlers) handler(message);
  };
  return channel;
}

/** Listen. The channel itself stays open — see the note above about outliving the React tree. */
export function subscribeToTabs(handler: TabMessageHandler): () => void {
  open();
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

/** Whether there is a channel at all. False makes the import a no-op rather than a broken press. */
export function tabsReachable(): boolean {
  return open() !== null;
}

export function postToTabs(message: TabMessage): void {
  try {
    open()?.postMessage({ ...message, v: PROTOCOL });
  } catch {
    // A payload the structured clone refused, or a channel the browser has taken away. Either way
    // this tab has said what it can, and the other side's own timeout is what answers the silence.
  }
}

/** A request id, and the tab id above: unique enough that two tabs cannot collide on one. */
export function newRequestId(): string {
  const source = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof source?.randomUUID === 'function') return source.randomUUID();
  // A browser without `randomUUID`, or a page that is not a secure context. What this id has to
  // survive is a handful of tabs in one browser rather than somebody trying to collide with it.
  return `${String(Date.now())}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Read a message off the channel, R4's shape applied to a carrier that is not HTTP: a payload that
 * does not read as one of the four messages is **dropped** rather than half-understood.
 *
 * The sender is same-origin by construction, so this is not a trust boundary — it is a *version*
 * boundary, which is why the protocol number is checked before anything else.
 */
function readMessage(value: unknown): TabMessage | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (record['v'] !== PROTOCOL) return null;
  const request = record['request'];
  if (typeof request !== 'string' || request === '') return null;

  switch (record['kind']) {
    case 'collect':
      return { kind: 'collect', request };
    case 'offer': {
      const tab = record['tab'];
      if (typeof tab !== 'string' || tab === '') return null;
      const videos = readQueueVideos(record['videos']);
      // An offer with nothing in it is not an offer: the tab that sent it has nothing to hand over,
      // and it must not end up in the `take` that closes tabs.
      return videos.length === 0 ? null : { kind: 'offer', request, tab, videos };
    }
    case 'take': {
      const tabs = record['tabs'];
      if (!Array.isArray(tabs)) return null;
      return {
        kind: 'take',
        request,
        tabs: tabs.filter((tab): tab is string => typeof tab === 'string'),
      };
    }
    case 'stayed': {
      const tab = record['tab'];
      if (typeof tab !== 'string' || tab === '') return null;
      return { kind: 'stayed', request, tab };
    }
    default:
      return null;
  }
}
